import React, { useEffect, useState, useMemo } from "react";
import "./App.css";
import NewAdmission from "./NewAdmission";
import RenewAdmission from "./RenewAdmission";
import RearrangeSeat from "./RearrangeSeat"; 
import AmendStudent from "./AmendStudent";
import CreateSeat from "./CreateSeat"; 
import Reports from "./Reports";
import StudentEnquiryComponent from './StudentEnquiryComponent';
import Login from "./Login";
import RenewAdmissionList from "./RenewAdmissionList"; 
import EmailBroadcast from "./EmailBroadcast"; 
import AdmissionsReport from "./AdmissionsReport"; 
import { apiService } from "./services/apiService"; 
import { 
  FaHome, 
  FaPlus, 
  FaSync, 
  FaChair, 
  FaEdit, 
  FaFileAlt, 
  FaSignOutAlt, 
  FaPlusSquare, 
  FaEnvelopeOpenText, 
  FaQuestionCircle 
} from "react-icons/fa";

function App() {
  const [token, setToken] = useState(sessionStorage.getItem("token") || null);
  
  // Dynamic Library Name state loaded from storage
  const [clientName, setClientName] = useState(
    sessionStorage.getItem("clientName") || "Wisdom Library"
  );

  // Client ID state loaded dynamically from storage
  const [clientId, setClientId] = useState(
    sessionStorage.getItem("clientId") || "CLIENT_01"
  );
  
  // Safely initialize roleId, ignoring literal "null" or "undefined" strings
  const [roleId, setRoleId] = useState(() => {
    const savedRole = sessionStorage.getItem("roleId");
    return (savedRole && savedRole !== "undefined" && savedRole !== "null") ? savedRole : null;
  });
  
  const [stats, setStats] = useState({ 
    totalSeats: 0, 
    bookedSeats: 0, 
    availableSeats: 0, 
    totalCapacity: 0, 
    currentCapacityCount: 0, 
    remainCapacityCount: 0, 
    maleCount: 0, 
    femaleCount: 0 
  });
  
  const [seats, setSeats] = useState([]);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [seatForAdmission, setSeatForAdmission] = useState(null);
  const [seatAdmissions, setSeatAdmissions] = useState([]);
  const [page, setPage] = useState("dashboard");
  const [seatPopupPos, setSeatPopupPos] = useState(null);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = () => {
    apiService.getDashboardStats()
      .then(setStats)
      .catch((err) => {
        console.error("Error loading stats:", err);
        if (err.message && (err.message.includes("401") || err.message.includes("Session expired"))) {
          handleLogout();
        }
      });

    apiService.getSeats()
      .then((data) => setSeats(data || []))
      .catch((err) => {
        console.error("Error loading seats:", err);
        if (err.message && (err.message.includes("401") || err.message.includes("Session expired"))) {
          handleLogout();
        }
      });
  };

  // Safe login handler accepting user context parameters
  const handleLoginSuccess = (userToken, userRoleId, userClientName, userClientId) => {
    if (!userToken) {
      console.error("Login succeeded but no token was provided!");
      return;
    }

    sessionStorage.setItem("token", userToken);
    setToken(userToken);

    if (userClientName) {
      sessionStorage.setItem("clientName", userClientName);
      setClientName(userClientName);
    }

    if (userClientId) {
      sessionStorage.setItem("clientId", userClientId);
      setClientId(userClientId);
    }

    if (userRoleId !== undefined && userRoleId !== null) {
      const normalizedRole = String(userRoleId);
      sessionStorage.setItem("roleId", normalizedRole);
      setRoleId(normalizedRole);
    } else {
      sessionStorage.removeItem("roleId");
      setRoleId(null);
    }
    
    setPage("dashboard");
  };

  const handleLogout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("roleId");
    sessionStorage.removeItem("clientName");
    sessionStorage.removeItem("clientId");
    
    setToken(null);
    setRoleId(null);
    setClientName(" ");
    setSelectedSeat(null);
  };

  // Convert role helper variables
  const isAdmin = roleId === "1";
  const isStaff = roleId === "2";

  // Role authorization guards
  const canManageAdmissions = isAdmin || isStaff || !roleId;
  const showAdminFeatures = isAdmin || !roleId;

  const getSeatClass = (seat) => {
    if (seat.blocked) return "blocked";
    if (seat.status === "RENEWAL_PENDING") return "renewal";
    if (seat.status === "EXPIRED") return "expired";
    if (seat.currentCount === 0) return "available";
    if (seat.currentCount === 1) return "partial";
    if (seat.currentCount >= seat.maxCapacity) return "full";
    return "available";
  };

  const sortSeats = (seatList) => {
    return [...seatList].sort((a, b) => {
      const codeA = a.seatCode || "";
      const codeB = b.seatCode || "";
      const alphaA = codeA.replace(/[0-9]/g, "");
      const alphaB = codeB.replace(/[0-9]/g, "");
      
      if (alphaA !== alphaB) {
        return alphaA.localeCompare(alphaB);
      }
      const numA = parseInt(codeA.replace(/\D/g, "") || "0", 10);
      const numB = parseInt(codeB.replace(/\D/g, "") || "0", 10);
      return numA - numB;
    });
  };

  const sortedSeats = sortSeats(seats);

  // Dynamic Grouping of Seats by Starting Alphabet / Prefix
  const groupedSeats = useMemo(() => {
    return sortedSeats.reduce((acc, seat) => {
      const code = (seat.seatCode || "").trim();
      const prefixMatch = code.match(/^[A-Za-z]+/);
      const prefix = prefixMatch ? prefixMatch[0].toUpperCase() : "Other";

      if (!acc[prefix]) {
        acc[prefix] = [];
      }
      acc[prefix].push(seat);
      return acc;
    }, {});
  }, [sortedSeats]);

  const groupKeys = Object.keys(groupedSeats);

  if (!token || token === "undefined" || token === "null") {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="app">
      {/* ===== TOP BAR ===== */}
      <div className="top-container">
        <header className="header">
          {clientName}
        </header>
        
        {/* Unified multi-row navigation layout */}
        <nav className="navbar multi-row-nav">
          
          {/* Row 1: Primary Core Action Items */}
          <div className="nav-row row-primary">
            <button className={`nav-button ${page === "dashboard" ? "active" : ""}`} onClick={() => setPage("dashboard")}>
              <FaHome className="nav-icon" /> Dashboard
            </button>
            
            {canManageAdmissions && (
              <>
                <button className={`nav-button ${page === "new" ? "active" : ""}`} onClick={() => setPage("new")}>
                  <FaPlus className="nav-icon" /> New Admission
                </button>
                <button className={`nav-button ${page === "renew" ? "active" : ""}`} onClick={() => setPage("renew")}>
                  <FaSync className="nav-icon" /> Renew Admission
                </button>
              </>
            )}

            {showAdminFeatures && (
              <>
                <button className={`nav-button ${page === "createSeat" ? "active" : ""}`} onClick={() => setPage("createSeat")}>
                  <FaPlusSquare className="nav-icon" /> Create Seat
                </button>
                <button className={`nav-button ${page === "rearrangeSeat" ? "active" : ""}`} onClick={() => setPage("rearrangeSeat")}>
                  <FaChair className="nav-icon" /> Rearrange Seat
                </button>
                <button className={`nav-button ${page === "amendStudent" ? "active" : ""}`} onClick={() => setPage("amendStudent")}>
                  <FaEdit className="nav-icon" /> Amend Student
                </button>
              </>
            )}
          </div>

          {/* Row 2: Secondary Administrative / Reporting tools & Logout */}
          <div className="nav-row row-secondary">
            {showAdminFeatures && (
              <>
                <button className={`nav-button ${page === "emailBroadcast" ? "active" : ""}`} onClick={() => setPage("emailBroadcast")}>
                  <FaEnvelopeOpenText className="nav-icon" /> Broadcast Email
                </button>
                <button className={`nav-button ${page === "reports" ? "active" : ""}`} onClick={() => setPage("reports")}>
                  <FaFileAlt className="nav-icon" /> Fee Report
                </button>
              </>
            )}

            {canManageAdmissions && (
              <button className={`nav-button ${page === "renewadmissionlist" ? "active" : ""}`} onClick={() => setPage("renewadmissionlist")}>
                <FaFileAlt className="nav-icon" /> Get Renew List
              </button>
            )}

            {/* Admissions Report Navigation Option */}
            {canManageAdmissions && (
              <button className={`nav-button ${page === "admissionsReport" ? "active" : ""}`} onClick={() => setPage("admissionsReport")}>
                <FaFileAlt className="nav-icon" /> Admissions Report
              </button>
            )}

            {/* Student Enquiry Navigation Option */}
            {canManageAdmissions && (
              <button className={`nav-button ${page === "studentEnquiryComponent" ? "active" : ""}`} onClick={() => setPage("studentEnquiryComponent")}>
                <FaQuestionCircle className="nav-icon" /> Enquiry
              </button>
            )}

            <button className="nav-button logout-btn" onClick={handleLogout} style={{ marginLeft: "auto", background: "#d32f2f" }}>
              <FaSignOutAlt className="nav-icon" /> Logout
            </button>
          </div>
        </nav>
      </div>

      {/* ================= DASHBOARD ================= */}
      {page === "dashboard" && (
        <div className="dashboard-content">
          <div className="stats">
            <div className="stat-card">
              <div className="stat-icon total"><FaChair /></div>
              <div className="stat-label">Total Seats / Desks</div>
              <div className="stat-value">{stats.totalSeats}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon booked"><FaEdit /></div>
              <div className="stat-label">Booked Seats</div>
              <div className="stat-value">{stats.bookedSeats}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon avail"><FaHome /></div>
              <div className="stat-label">Available Seats</div>
              <div className="stat-value">{stats.availableSeats}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon total"><FaHome /></div>
              <div className="stat-label">Total Admission Capacity </div>
              <div className="stat-value">{stats.totalCapacity}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon booked"><FaEdit /></div>
              <div className="stat-label">Occupied Capacity Admissions </div>
              <div className="stat-value">{stats.currentCapacityCount}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon avail"><FaEdit /></div>
              <div className="stat-label">Remaining Capacity Admissions</div>
              <div className="stat-value">{stats.remainCapacityCount}</div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px", marginBottom: "16px" }}>
            <div className="layout-heading" style={{ margin: 0 }}>
              All Seats/Admissions Dashboard
            </div>
            
            <div className="legend" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", margin: "0 auto", padding: 0, background: "transparent", boxShadow: "none", border: "none" }}>
              <div className="legend-item" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "600", color: "#4a5568" }}>
                <div className="seat available" style={{ width: "24px", height: "24px", borderRadius: "4px", display: "inline-block" }} />
                Available
              </div>
              <div className="legend-item" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "600", color: "#4a5568" }}>
                <div className="seat partial" style={{ width: "24px", height: "24px", borderRadius: "4px", display: "inline-block" }} />
                Partially Occupied
              </div>
              <div className="legend-item" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "600", color: "#4a5568" }}>
                <div className="seat full" style={{ width: "24px", height: "24px", borderRadius: "4px", display: "inline-block" }} />
                Fully Occupied
              </div>
              <div className="legend-item" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "600", color: "#4a5568" }}>
                <div className="seat renewal" style={{ width: "24px", height: "24px", borderRadius: "4px", display: "inline-block" }} />
                Renewal Pending
              </div>
              <div className="legend-item" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "600", color: "#4a5568" }}>
                <div className="seat expired" style={{ width: "24px", height: "24px", borderRadius: "4px", display: "inline-block" }} />
                Expired
              </div>
            </div>

            <div style={{ width: "150px" }} className="dashboard-aligner-hide-mobile" />
          </div>
          
          <div className="unified-seat-grid-container" style={{ background: "#fff", borderRadius: "8px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", marginBottom: "30px" }}>
            {groupKeys.map((groupPrefix, idx) => (
              <React.Fragment key={groupPrefix}>
                {idx > 0 && (
                  <hr style={{ border: "none", borderTop: "2px solid #e2e8f0", margin: "24px 0 16px 0" }} />
                )}

                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                  <span style={{ fontWeight: "bold", fontSize: "16px", color: "#2d3748" }}>
                    {groupPrefix === "Other" ? "Other Seats" : `Section ${groupPrefix}`}
                  </span>
                  <span style={{ fontSize: "12px", background: "#edf2f7", color: "#4a5568", padding: "2px 8px", borderRadius: "10px", fontWeight: "600" }}>
                    {groupedSeats[groupPrefix].length} seats
                  </span>
                </div>

                <div className="unified-seat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))", gap: "12px" }}>
                  {groupedSeats[groupPrefix].map((seat) => (
                    <div
                      key={seat.seatId}
                      className={`seat ${getSeatClass(seat)}`}
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50px", fontWeight: "bold", borderRadius: "6px", cursor: "pointer", userSelect: "none" }}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const popupWidth = 520; const popupHeight = 360; const padding = 12;
                        let top = rect.top; let left = rect.right + padding;
                        if (left + popupWidth > window.innerWidth) left = rect.left - popupWidth - padding;
                        if (left < padding) left = padding;
                        if (top + popupHeight > window.innerHeight) top = window.innerHeight - popupHeight - padding;
                        if (top < padding) top = padding;
                        setSeatPopupPos({ top, left });
                        
                        apiService.getAdmissionsBySeat(seat.seatId)
                          .then(setSeatAdmissions)
                          .catch(() => setSeatAdmissions([]));
                          
                        setSelectedSeat(seat);
                      }}
                    >
                      {seat.seatCode}
                    </div>
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* ===== OTHER PAGES ===== */}
      {page === "createSeat" && showAdminFeatures && (
        <CreateSeat 
          onSuccess={() => { loadData(); setPage("dashboard"); }} 
          onBack={() => setPage("dashboard")} 
        />
      )}
      {page === "rearrangeSeat" && showAdminFeatures && (
        <RearrangeSeat token={token} onSuccess={() => { loadData(); setPage("dashboard"); }} />
      )}
      {page === "amendStudent" && showAdminFeatures && (
        <AmendStudent token={token} onSuccess={() => { loadData(); setPage("dashboard"); }} />
      )}
      {page === "reports" && showAdminFeatures && (
        <Reports token={token} onBack={() => setPage("dashboard")} />
      )}
      
      {page === "emailBroadcast" && showAdminFeatures && (
        <EmailBroadcast onBack={() => setPage("dashboard")} />
      )}

      {page === "admissionsReport" && canManageAdmissions && (
        <AdmissionsReport token={token} onBack={() => setPage("dashboard")} />
      )}

      {/* Render Student Enquiry View */}
      {page === "studentEnquiryComponent" && canManageAdmissions && (
        <StudentEnquiryComponent 
          clientId={clientId} 
          onBack={() => setPage("dashboard")} 
        />
      )}

      {page === "new" && canManageAdmissions && (
        <NewAdmission token={token} seat={seatForAdmission} onSuccess={() => { setSeatForAdmission(null); loadData(); setPage("dashboard"); }} />
      )}
      {page === "renew" && canManageAdmissions && (
        <RenewAdmission token={token} onSuccess={() => { loadData(); setPage("dashboard"); }} />
      )}
      {page === "renewadmissionlist" && canManageAdmissions && (
        <RenewAdmissionList token={token} onBack={() => setPage("dashboard")} />
      )}

      {/* ===== SEAT POPUP ===== */}
      {selectedSeat && seatPopupPos && page === "dashboard" && (
        <div className="seat-popup" style={{ top: Math.max(20, seatPopupPos.top - 50), left: seatPopupPos.left, position: "fixed", width: "520px", maxHeight: "80vh", overflowY: "auto", zIndex: 1000 }}>
          <div className="popup-content">
            <div className="popup-left">
              <h4>Seat Details</h4>
              <p><b>Seat:</b> {selectedSeat.seatCode}</p>
              <p><b>Floor:</b> {selectedSeat.floor}</p>
              <p><b>Section:</b> {selectedSeat.section}</p>
              <p><b>Capacity:</b> {selectedSeat.maxCapacity}</p>
              <p><b>Occupied:</b> {selectedSeat.currentCount}</p>
            </div>
            <div className="popup-right">
              <h4>Active Students</h4>
              {seatAdmissions.length === 0 ? <p>No active admissions</p> : seatAdmissions.map((ad, index) => (
                <div key={index} className="student-block">
                  <p><b>Name:</b> {ad.studentName}</p>
                  <p><b>Contact:</b> {ad.contact}</p>
                  <p><b>Admission:</b> {new Date(ad.startDate).toLocaleDateString("en-GB")}</p>
                  <p><b>Expiry:</b> {new Date(ad.endDate).toLocaleDateString("en-GB")}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="popup-actions">
            {canManageAdmissions && (
              <button onClick={() => {
                if (selectedSeat.status === "RENEWAL_PENDING") { setSelectedSeat(null); setSeatPopupPos(null); setPage("renew"); } 
                else { setSeatForAdmission(selectedSeat); setSelectedSeat(null); setSeatPopupPos(null); setPage("new"); }
              }}>{selectedSeat.status === "RENEWAL_PENDING" ? "Renew Admission" : "New Admission"}</button>
            )}
            <button onClick={() => setSelectedSeat(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;